import { createSelector, OutputSelector } from 'reselect';
import { ActionReducer } from '@ngrx/store';
import * as fromRouter from '@ngrx/router-store';
import {Action} from '@ngrx/store';
import * as auth from '../actions/authentication';
import * as contact from '../actions/contact';
import * as list from '../actions/list';
import * as log from '../actions/log';
import * as register from '../actions/register';
import * as setting from '../actions/setting';
import * as user from '../actions/user';
import {ErrorLog} from '../models/error-log';
import {PagingModel} from '../models/paging-model';
import {Setting} from '../models/setting';
import {Contact} from '../models/contact';
import {ListItem} from '../models/list-item';
import {AccessRequest} from '../models/access-request';
import {User} from '../models/user';
import {KvItem} from '../models/kv-item';
import {Observable} from 'rxjs/Observable';
import {UserListModel} from '../models/user-list-model';
import { compose } from '@ngrx/store';
import { combineReducers } from '@ngrx/store';

import * as fromLog from './log';
import * as fromSetting from './setting';
import * as fromList from './list';
import * as fromContact from './contact';
import * as fromRegister from './register';
import * as fromUser from './user';
import * as fromAuth from './authentication';
import * as fromRoot from '../../../app.reducer';
import { createFeatureSelector } from '@ngrx/store';

/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface CoreState {
  log: fromLog.State;
  setting: fromSetting.State;
  list: fromList.State;
  contact: fromContact.State;
  register: fromRegister.State;
  user: fromUser.State;
  authentication: fromAuth.State;
}

export interface State extends fromRoot.State {
  core: CoreState;
}

export const initialState: CoreState = {
  log: fromLog.initialState,
  setting: fromSetting.initialState,
  list: fromList.initialState,
  contact: fromContact.initialState,
  register: fromRegister.initialState,
  user: fromUser.initialState,
  authentication: fromAuth.initialState
};

/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */
export const reducers = {
  log: fromLog.reducer,
  setting: fromSetting.reducer,
  list: fromList.reducer,
  contact: fromContact.reducer,
  register: fromRegister.reducer,
  user: fromUser.reducer,
  authentication: fromAuth.reducer,
  router: fromRouter.routerReducer
};

export const getCoreState = createFeatureSelector<CoreState>('core');

export const getLogState = createSelector(getCoreState, (state: CoreState) => state.log);
export const getSettingState = createSelector(getCoreState, (state: CoreState) => state.setting);
export const getListState = createSelector(getCoreState, (state: CoreState) => state.list);
export const getContactState = createSelector(getCoreState, (state: CoreState) => state.contact);
export const getRegisterState = createSelector(getCoreState, (state: CoreState) => state.register);
export const getUserState = createSelector(getCoreState, (state: CoreState) => state.user);
export const getAuthenticationState = createSelector(getCoreState, (state: CoreState) => state.authentication);


export const getLogPage = createSelector(getLogState, fromLog.getLogPage);
export const getLogPaging = createSelector(getLogState, fromLog.getLogPaging);
export const getLogWorking = createSelector(getLogState, fromLog.getLogWorking);

export const getSettingPage = createSelector(getSettingState, fromSetting.getSettingPage);
export const getSettingPaging = createSelector(getSettingState, fromSetting.getSettingPaging);
export const getCurrentSetting = createSelector(getSettingState, fromSetting.getCurrentSetting);
export const getSettingWorking = createSelector(getSettingState, fromSetting.getSettingWorking);

export const getListPage = createSelector(getListState, fromList.getListPage);
export const getListPaging = createSelector(getListState, fromList.getlistPaging);
export const getCurrentListItem = createSelector(getListState, fromList.getCurrentListItem);
export const getListId = createSelector(getListState, fromList.getListId);
export const getCurrentListItemId = createSelector(getListState, fromList.getCurrentListItemId);
export const getStateList = createSelector(getListState, fromList.getStateList);
export const getListWorking = createSelector(getListState, fromList.getListWorking);

export const getContactPage = createSelector(getContactState, fromContact.getContactPage);
export const getContactPaging = createSelector(getContactState, fromContact.getContactPaging);
export const getCurrentContact = createSelector(getContactState, fromContact.getCurrentContact);
export const getContactWorking = createSelector(getContactState, fromContact.getContactWorking);

export const getRequestList = createSelector(getRegisterState, fromRegister.getRequestList);
export const getCurrentRequest = createSelector(getRegisterState, fromRegister.getCurrentRequest);
export const getRequestSubmitted = createSelector(getRegisterState, fromRegister.getRequestSubmitted);
export const getRequestType = createSelector(getRegisterState, fromRegister.getRequestType);
export const getRequestWorking = createSelector(getRegisterState, fromRegister.getRequestWorking);
export const getRequestApproveId = createSelector(getRegisterState, fromRegister.getRequestApproveId);
export const getRequestDenyId = createSelector(getRegisterState, fromRegister.getRequestDenyId);

export const getUserPage = createSelector(getUserState, fromUser.getUserPage);
export const getUserPaging = createSelector(getUserState, fromUser.getUserPaging);
export const getSelectedUsername = createSelector(getUserState, fromUser.getSelectedUsername);
export const getSelectedUser = createSelector(getUserState, fromUser.getSelectedUser);
export const getUserRoles = createSelector(getUserState, fromUser.getUserRoles);
export const getIsNewUser = createSelector(getUserState, fromUser.getIsNewUser);
export const getUserWorking = createSelector(getUserState, fromUser.getUserWorking);
export const getUsernameExists = createSelector(getUserState, fromUser.getUsernameExists);
export const getEmailExists = createSelector(getUserState, fromUser.getEmailExists);


export const getAuthIsAliasing = createSelector(getAuthenticationState, fromAuth.getAuthIsAliasing);
export const getAuthLoggedIn = createSelector(getAuthenticationState, fromAuth.getAuthLoggedIn);
export const getAuthWorking = createSelector(getAuthenticationState, fromAuth.getAuthWorking);
export const getAuthFirstName = createSelector(getAuthenticationState, fromAuth.getAuthFirstName);
export const getAuthLastName = createSelector(getAuthenticationState, fromAuth.getAuthLastName);
export const getAuthFullName = createSelector(getAuthenticationState, fromAuth.getAuthFullName);
export const getAuthRole = createSelector(getAuthenticationState, fromAuth.getAuthRole);
export const getAuthUsername = createSelector(getAuthenticationState, fromAuth.getAuthUsername);
export const getAuthIsAdmin = createSelector(getAuthenticationState, fromAuth.getAuthIsAdmin);
export const getAuthIsSuperAdmin = createSelector(getAuthenticationState, fromAuth.getAuthIsSuperAdmin);
export const getAuthMustChangePassword = createSelector(getAuthenticationState, fromAuth.getAuthMustChangePassword);
export const getAuthIsUser = createSelector(getAuthenticationState, fromAuth.getAuthIsUser);
export const getIsError = createSelector(getAuthenticationState, fromAuth.getIsError);
export const getMessage = createSelector(getAuthenticationState, fromAuth.getMessage);
export const getIsSuccess = createSelector(getAuthenticationState, fromAuth.getIsSuccess);
export const getProgress = createSelector(getAuthenticationState, fromAuth.getProgress);
export const getHasAcceptedAgreement = createSelector(getAuthenticationState, fromAuth.getHasAcceptedAgreement);



// WEBPACK FOOTER //
// C:/Users/jstan/Projects/usda-document-client/src/app/modules/core/reducers/index.ts